import React, { useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  Alert,
  Tooltip,
  IconButton,
  useTheme
} from '@mui/material'
import { ECGModal } from './components/ECGModal'
import { ECGLeadRow } from './components/ECGLeadRow'
import { ECGLegend } from './components/ECGLegend'
import { ECGTechnicalInfo } from './components/ECGTechnicalInfo'
import { useECGModal } from './hooks/useECGModal'
import { useECGHighlight } from './hooks/useECGHighlight'
import {
  ECG_LEAD_CHANNELS,
  RHYTHM_STRIP_HEIGHT,
  STANDARD_LEAD_HEIGHT,
  ECG_LEAD_ORDER,
  ECGLeadName
} from './constants/ecgLayout'
import { IExamResponse } from 'types/exam/types'
import { WarningAmber } from '@mui/icons-material'

interface IECGExamProps {
  exameData: IExamResponse
}

/**
 * Componente principal de exibição de ECG de 12 derivações
 * Organizado em 3 canais de 4 derivações + faixa de ritmo
 */
const ECGExamComponent = ({ exameData }: IECGExamProps) => {
  const { modalOpen, selectedLead, openModal, closeModal } = useECGModal()
  const { isHighlighted } = useECGHighlight(exameData)
  const ecgRawData = exameData.ecg_raw_data
  const ecgAvgWaveData = exameData.ecg_avg_waves_data

  const theme = useTheme()

  const bpm = useMemo(() => {
    const exam_result = exameData.exam_results.find(result => {
      return result.output_kind === 'Result' && result.result_type === 'bpm'
    })

    if (exam_result) {
      return exam_result.result_value * 1000
    }

    return undefined
  }, [exameData.exam_results])
  // Criar configuração do lead
  const createLeadConfig = useCallback(
    (leadProperty: string) => {
      if (!ecgRawData) {
        return {
          name: leadProperty,
          data: [],
          highlight: false,
          onClick: () => {}
        }
      }

      const leadData = ecgRawData[leadProperty as keyof typeof ecgRawData]
      const values = Array.isArray(leadData) ? leadData : []

      // Converter lead_i para I, lead_ii para II, etc para exibição
      const displayName = leadProperty.replaceAll('lead_', '').toUpperCase()

      return {
        name: displayName,
        data: values,
        highlight: isHighlighted(displayName),
        onClick: () =>
          openModal(
            displayName,
            values,
            isHighlighted(displayName),
            ecgAvgWaveData?.[ECG_LEAD_ORDER[leadProperty as ECGLeadName]]
          )
      }
    },
    [ecgRawData, isHighlighted, openModal, ecgAvgWaveData]
  )

  // Memoizar configurações dos canais com sample rate específico
  const channel1Leads = useMemo(
    () =>
      ECG_LEAD_CHANNELS.CHANNEL_1.map(lead => ({
        ...createLeadConfig(lead)
      })),
    [createLeadConfig]
  )

  const channel2Leads = useMemo(
    () =>
      ECG_LEAD_CHANNELS.CHANNEL_2.map(lead => ({
        ...createLeadConfig(lead)
      })),
    [createLeadConfig]
  )

  const channel3Leads = useMemo(
    () =>
      ECG_LEAD_CHANNELS.CHANNEL_3.map(lead => ({
        ...createLeadConfig(lead)
      })),
    [createLeadConfig]
  )

  const rhythmStripLeads = useMemo(
    () =>
      ECG_LEAD_CHANNELS.RHYTHM.map(lead => ({
        ...createLeadConfig(lead),
        isRhythmStrip: true
      })),
    [createLeadConfig]
  )

  // Se não houver dados ECG
  if (!ecgRawData) {
    return (
      <Box sx={{ width: '100%', py: 2 }}>
        <Alert severity='info'>Dados de ECG não disponíveis</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', minWidth: { xs: 'unset', md: 'unset' } }}>
      <Typography
        variant='h6'
        sx={{
          mb: 2,
          color: 'var(--primarySoft-button-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2
        }}
      >
        Eletrocardiograma - 12 Derivações
        <Tooltip
          title='Usar somente com ECG de 12 derivações. Qualquer outro uso ou maneira de manipular o sistema não resultará nos resultados esperados e o usuário será responsável por resultados e decisões tomadas, eximindo a swissanalytica/sentinela de qualquer responsabilidade'
          placement='top-start'
          PopperProps={{
            modifiers: [
              {
                name: 'preventOverflow',
                options: { padding: 8 }
              },
              {
                name: 'flip',
                enabled: true
              }
            ]
          }}
          slotProps={{
            popper: {
              sx: {
                '& .MuiTooltip-tooltip': {
                  maxWidth: 400,
                  padding: '8px 12px',
                  backgroundColor: theme.palette.warning.main,
                  color: theme.palette.warning.contrastText,
                  fontSize: '0.7rem'
                }
              }
            }
          }}
        >
          <IconButton
            size='small'
            sx={{
              color: 'warning.main',
              alignSelf: 'flex-start'
            }}
          >
            <WarningAmber />
          </IconButton>
        </Tooltip>
      </Typography>

      {/* Canal 1: I, aVR, V1, V4 */}
      <ECGLeadRow leads={channel1Leads} height={STANDARD_LEAD_HEIGHT} />

      {/* Canal 2: II, aVL, V2, V5 */}
      <ECGLeadRow leads={channel2Leads} height={STANDARD_LEAD_HEIGHT} />

      {/* Canal 3: III, aVF, V3, V6 */}
      <ECGLeadRow leads={channel3Leads} height={STANDARD_LEAD_HEIGHT} />

      {/* Canal 4: Faixa de Ritmo (DII longo) */}
      <ECGLeadRow leads={rhythmStripLeads} height={RHYTHM_STRIP_HEIGHT} />

      {/* Informações Técnicas do ECG */}
      <ECGTechnicalInfo durationSeconds={10} showBorder={false} bpm={bpm} />

      {/* Legendas */}
      <ECGLegend />

      {/* Modal para visualização ampliada */}
      {selectedLead && (
        <ECGModal
          open={modalOpen}
          onClose={closeModal}
          leadName={selectedLead.name}
          data={selectedLead.data}
          highlight={selectedLead.highlight}
          avgWave={selectedLead.avgWave}
          bpm={bpm}
        />
      )}
    </Box>
  )
}

// Memoizar componente para evitar rerenders desnecessários
export const ECGExam = React.memo(ECGExamComponent)

export default ECGExam
